import { useTranslation } from "react-i18next";
import moment from "moment";
import { useMemo, useState } from "react";
import DeleteModal from "../common/delete-modal";
import ActionsDropdown from "../common/actions-dropdown";
import InfinityTable from "../../../../../components/infinity-table/infinity-table";
import { TableCell } from "../../../../../components/table/table-cell/table-cell";
import { TableCellPicture } from "../../../../../components/table/table-cell/table-cell-picture";
import UserActivityStatus from "../../../../../components/user-activity-status/user-activity-status";
import AiDraftLabel from "../../../../../components/table/ai-draft-label";

export const AdminResourceTable = ({
  data,
  shouldDropCache,
  onHandleRowClick,
  remove,
  removeModalQuestion,
  tableColumnKeys = ["title", "updatedAt"],
  isLoading,
  updateSorting,
  toggleActive,
  hasMore,
  fetchData,
  selectionColumn,
  after,
  containerRef,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState();
  const [item, setItem] = useState();
  const [isDeactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deactivateItem, setDeactivateItem] = useState(null);

  const handleOpenDeactivateModal = (item) => {
    setDeactivateModalOpen(true);
    setDeactivateItem(item);
  };

  const handleCloseDeactivateModal = () => {
    setDeactivateModalOpen(false);
    setDeactivateItem(null);
  };

  const handleDeactivateConfirm = () => {
    if (deactivateItem?.id) {
      toggleActive(deactivateItem);
    }
    handleCloseDeactivateModal();
  };

  const handleOpenDeleteModal = (item) => {
    setModalOpen(true);
    setItem(item);
  };

  const handleCloseDeleteModal = () => {
    setModalOpen(false);
    setItem();
  };

  const handleOkPress = (id, articleTitle) => {
    handleCloseDeleteModal();
    remove(id, articleTitle);
  };

  const getColumnByKey = (key, { shouldDropCache }) => {
    switch (key) {
      case "cover":
        return {
          title: "Cover",
          dataIndex: "cover",
          key: "cover",
          render: (_, item) => (
            <TableCellPicture cacheKey={item.updatedAt} proportion={60} pageId={item.id} />
          ),
          fixed: "left",
        };

      case "categories":
        return {
          title: "Categories",
          dataIndex: "categories",
          key: "categories",
          render: (_, item) => <TableCell text={item?.Category?.name} />,
          width: "30%",
          sorter: true,
        };

      case "status":
        return {
          title: "Status",
          dataIndex: "status",
          key: "status",
          render: (_, item) => <UserActivityStatus style={{ whiteSpace: 'nowrap' }} userStatus={item?.status} />,
          width: "20%"
        };

      case "statusSummary":
        return {
          title: "Status",
          dataIndex: "statusSummary",
          key: "statusSummary",
          render: (_, item) => <UserActivityStatus style={{ whiteSpace: 'nowrap' }} userStatus={item?.statusSummary} />,
          width: "20%"
        };

      case "title":
        return {
          title: "Title",
          dataIndex: "title",
          key: "title",
          render: (_, item) => (
            <div>
              <TableCell title={item.title || item.name || item.text} />
              <AiDraftLabel record={item} />
            </div>
          ),
          width: "40%",
          sorter: true,
        };

      case "titleColorMark":
        return {
          title: "Title",
          dataIndex: "title",
          key: "title",
          render: (_, item) => (
            <div>
              <TableCell
                title={item?.title || item?.name || item?.text}
                markColor={item?.color || "#28282866"}
                subtext={item?.FactUrl?.title || item?.FactCitation?.source}
              />
              <AiDraftLabel record={item} />
            </div>
          ),
          width: "30%",
        };

      case "updatedAt":
        return {
          title: t("task_management.table.last_modified"),
          dataIndex: "updatedAt",
          render: (_, item) => (
            <span>
              {`${moment(item?.updatedAt)
                .utc()
                .format("DD MMM YYYY, HH:mm")}, UTC` || "N/A"}
            </span>
          ),
          key: "updatedAt",
          width: "30%",
          sorter: true,
        };
      case "action":
        return {
          title: "Action",
          dataIndex: "action",
          render: (_, item) => (
            <ActionsDropdown
              acrticle={item?.id}
              remove={() => handleOpenDeleteModal(item)}
              toggleStatus={() => handleOpenDeactivateModal(item)}
              isActive={item?.isActive}
            />
          ),
          key: "action",
          width: "20%",
        };

      default:
        break;
    }
  };

  const tableColumns = useMemo(() => {
    const columns = tableColumnKeys.map((key) => getColumnByKey(key, { shouldDropCache }));
    if (selectionColumn) {
      return [selectionColumn, ...columns];
    }
    return columns;
  }, [tableColumnKeys, handleCloseDeleteModal, handleOpenDeleteModal, selectionColumn]);

  const onChange = (pagination, filters, sorter) => {
    const sortField = sorter.field;
    let sortOrder;

    if (!sorter.order) {
      sortOrder = "ASC";
    } else {
      sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    }
    updateSorting(sortField, sortOrder);
  };

  return (
    <>
      <DeleteModal
        title={deactivateItem?.title}
        question={`Are you sure you want to ${deactivateItem?.isActive ? 'deactivate' : 'activate'} this item?`}
        handleOk={handleDeactivateConfirm}
        handleCancel={handleCloseDeactivateModal}
        isModalOpen={isDeactivateModalOpen}
      />
      <DeleteModal
        title={item?.title}
        question={removeModalQuestion}
        handleOk={() => handleOkPress(item?.id, (item?.title || item?.text))}
        handleCancel={handleCloseDeleteModal}
        isModalOpen={isModalOpen}
      />
      <InfinityTable
        data={data}
        tableColumns={tableColumns}
        onHandleRowClick={onHandleRowClick}
        isLoading={isLoading}
        onChange={onChange}
        hasMore={!!hasMore}
        fetchData={fetchData}
        after={after}
        containerRef={containerRef}
      />
    </>
  );
};

export default AdminResourceTable;
